import * as React from 'react';
import documentService, { IUploadResult } from 'src/dataServices/DocumentService';
import localeService from 'src/dataServices/LocaleService';
import Text from 'src/components/text/Text';

interface IFileUploaderProps {
  existingDocumentNames?: string[];
  onUploadComplete?: (result: IUploadResult) => void;
}

const MAX_UPLOAD_FILE_SIZE_BYTES = 2 * 1024 * 1024;

const FileUploader = ({ existingDocumentNames = [], onUploadComplete }: IFileUploaderProps): JSX.Element => {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const uploadDocumentsLabel = localeService.getText('ProfileV2', 'uploadDocumentations');

  const onUploadClick = () => {
    fileInputRef.current?.click();
  };

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    // Reset input so selecting the same file again still fires onChange.
    event.target.value = '';

    if (!selectedFile) {
      return;
    }

    if (selectedFile.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      onUploadComplete?.({
        documentName: selectedFile.name,
        documentId: '',
        success: false,
        fileSize: selectedFile.size,
        uploadErrorCode: 'fileTooLarge'
      });
      return;
    }

    const normalizedSelectedFileName = selectedFile.name.trim().toLocaleLowerCase();
    const isDuplicateFile = existingDocumentNames.some((documentName) =>
      documentName.trim().toLocaleLowerCase() === normalizedSelectedFileName
    );

    if (isDuplicateFile) {
      onUploadComplete?.({
        documentName: selectedFile.name,
        documentId: '',
        success: false,
        fileSize: selectedFile.size,
        uploadErrorCode: 'duplicateFile'
      });
      return;
    }

    try {
      const response = await documentService.uploadDocument(selectedFile);
      const success = response.value !== undefined && !response.error;
      const uploadResult: IUploadResult = {
        documentName: selectedFile.name,
        documentId: response.value ?? '',
        success,
        fileSize: selectedFile.size,
        uploadErrorCode: success ? undefined : 'uploadFailed'
      };
      onUploadComplete?.(uploadResult);
    } catch {
      onUploadComplete?.({
        documentName: selectedFile.name,
        documentId: '',
        success: false,
        fileSize: selectedFile.size,
        uploadErrorCode: 'uploadFailed'
      });
    }
  };

  return (
    <div className="supporting-documents__uploader">
      <button className="supporting-documents__upload-button" type="button" onClick={onUploadClick} aria-label={uploadDocumentsLabel}>
        <Text category="ProfileV2" id="uploadDocumentations" />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        onChange={onFileChange}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default FileUploader;
